import {
  ERRORS,
  EstimateResult,
  TerminalContact,
  TransitDetails,
} from '../forms/TransitTimeCalculatorForm'
import { SimpleButton as Button } from '../shared/SimpleButton'
import GetEstimateIcon from '../icons/GetEstimateIcon'
import AlertIcon from '../icons/AlertIcon'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Skeleton from 'react-loading-skeleton'

type HeaderContent = {
  headerText: string
  detailText?: React.ReactNode
  bodyText?: React.ReactNode
}

const getErrorTextContent = (
  error: string,
  transitDetails: TransitDetails
): HeaderContent => {
  const { ORIGIN_ZIP_ERROR, DEST_ZIP_ERROR } = ERRORS
  const { deliveryZip, pickupZip } = transitDetails
  let invalidZip

  switch (error) {
    case ORIGIN_ZIP_ERROR:
      invalidZip = pickupZip
      break
    case DEST_ZIP_ERROR:
      invalidZip = deliveryZip
      break
  }

  const headerText = 'Transit times are only available for direct locations'
  const detailText = undefined
  const bodyText: React.ReactNode = (
    <span>
      <strong>{invalidZip}</strong> is not available within our direct service
      area. For help shipping non-direct please call 303-295-0300 or try again
      by entering an origin and destination zip within our direct{' '}
      <Link className="text-blue" href="/service-area">
        service area
      </Link>
      .
    </span>
  )

  return { headerText, detailText, bodyText }
}

const getResultTextContent = (
  destinationTerminalContact: TerminalContact,
  originTerminalContact: TerminalContact,
  serviceDays: number,
  weekends: number,
  holidays: number,
  destinationServicePontService: string,
  originServicePointService: string
): HeaderContent => {
  const hasNonBusinessDays = weekends || holidays

  const headerText = `${serviceDays} Day estimated transit time`
  const detailText: React.ReactNode = (
    <>
      <span className="whitespace-nowrap">
        {originServicePointService}
        &nbsp;<strong>&#8594;</strong>&nbsp;
      </span>
      <span className="block xsmMd:inline-flex">
        {destinationServicePontService}
      </span>
    </>
  )
  const bodyText = hasNonBusinessDays
    ? 'This estimate excludes day of pickup, weekends, and holidays.'
    : undefined

  return { headerText, detailText, bodyText }
}

const openContactModal = () => {
  document.getElementById('contact-modal')?.click()
}

const formatDate = (inputDate: string): string => {
  const [year, month, day] = inputDate.split('T')[0].split('-').map(String)
  return `${month}/${day}/${year}`
}

const formatPhoneNumber = (phoneNum: string): string => {
  const areaCode = phoneNum.slice(0, 3)
  const first3Digits = phoneNum.slice(3, 6)
  const last4Digits = phoneNum.slice(6)

  return `${areaCode}-${first3Digits}-${last4Digits}`
}

const getTerminalContactText = (terminalContact: TerminalContact): string => {
  const phone = formatPhoneNumber(terminalContact.phone)
  return `${terminalContact.line1}\n${terminalContact.city}, ${terminalContact.state}, ${terminalContact.zip}\n${phone}`
}

interface TransitTimeCalculatorResultProps {
  loading: boolean
  result:
    | (EstimateResult & { submittedTransitDetails: TransitDetails })
    | Record<string, never>
}

const ResultHeaderCard: React.FunctionComponent<
  TransitTimeCalculatorResultProps & { showError: boolean }
> = ({ loading, result, showError }) => {
  const {
    destinationTerminalContact,
    error,
    originTerminalContact,
    serviceDays,
    submittedTransitDetails,
    weekendDays,
    holidayDays,
    destinationServicePointServiceString,
    originServicePointServiceString,
  } = result
  const router = useRouter()

  const { headerText, detailText, bodyText } = showError
    ? getErrorTextContent(error, submittedTransitDetails)
    : getResultTextContent(
        destinationTerminalContact,
        originTerminalContact,
        serviceDays,
        weekendDays,
        holidayDays,
        destinationServicePointServiceString,
        originServicePointServiceString
      )

  const containerBgColor = showError ? 'bg-orange-20' : 'bg-green-light'
  const fontColor = showError ? 'text-orange-60' : 'text-green-success'
  const bodyFontColor = showError ? 'text-orange-100' : 'text-green-success'
  const icon = showError ? <AlertIcon /> : <GetEstimateIcon />
  const borderRadius = 'rounded-md'

  return (
    <div
      className={`relative flex px-4 py-5 mb-14 ${containerBgColor} ${borderRadius}`}
    >
      <span className="inline-flex p-[3px] mr-1 sm:mr-3">{icon}</span>
      <div className="inline-flex flex-col flex-grow sm:flex-row">
        <div className="flex flex-col content-center flex-grow sm:mr-5 lg:flex-wrap lg:flex-row">
          <h2
            className={`self-start font-semibold text-[20px] leading-[1.2] tracking-[0.015em] sm:text-[24px] sm:tracking-wide lg:mr-9 ${fontColor}`}
          >
            {headerText}
          </h2>
          {detailText && (
            <p
              className={`self-start text-[14px] mt-1 leading-[1.4] font-semibold sm:text-[16px] sm:leading-normal sm:mt-0 lg:self-center ${fontColor}`}
            >
              {detailText}
            </p>
          )}
          {bodyText && (
            <p
              className={`basis-full ${bodyFontColor} ${
                showError
                  ? 'mt-2 text-[16px] lg:leading-[1.2] lg:tracking-wider'
                  : 'text-[14px] mt-3 leading-[1.4] sm:mt-2 md:py-0.5 lg:mt-0'
              }`}
            >
              {bodyText}
            </p>
          )}
        </div>
        {!showError && (
          <span className="self-start mt-5 sm:self-center sm:mt-0">
            <Button
              buttonText="Schedule A Pickup"
              handleClick={() => router.push('schedule-pickup')}
            />
          </span>
        )}
      </div>

      {loading && (
        <Skeleton
          containerClassName={`absolute inset-0 flex ${borderRadius}`}
        />
      )}
    </div>
  )
}

const ErrorResultBody: React.FunctionComponent = () => {
  return (
    <p className="mt-14">
      For information on domestic transit times within the US, please&nbsp;
      <button
        className="cursor-pointer text-blue"
        onClick={() => openContactModal()}
      >
        reach out to us
      </button>
      . We can help with specific details on transit durations and associated
      costs.
    </p>
  )
}

const SuccessResultBody: React.FunctionComponent<
  TransitTimeCalculatorResultProps
> = ({ loading, result }) => {
  const {
    firstAvailablePickupDate,
    deliveryDate,
    originTerminalContact,
    destinationTerminalContact,
  } = result
  return (
    <>
      <div className="whitespace-pre xsm:flex xsm:flex-row xsm:flex-wrap xsmMd:grid xsmMd:grid-cols-2 lg:grid-cols-none lg:grid-flow-col lg:auto-cols-min">
        <div className="mb-5 mr-20">
          <h3 className="text-base font-semibold">Pickup Date</h3>
          <p className="text-base">
            {loading ? <Skeleton /> : formatDate(firstAvailablePickupDate)}
          </p>
        </div>
        <div className="mb-5 xsmMd:mr-20">
          <h3 className="text-base font-semibold">Delivery Date</h3>
          <p className="text-base">
            {loading ? <Skeleton /> : formatDate(deliveryDate)}
          </p>
        </div>
        <div className="mb-5 mr-20">
          <h3 className="text-base font-semibold">Origin Service Center</h3>
          <p className="text-base">
            {loading ? (
              <Skeleton count={3} />
            ) : (
              getTerminalContactText(originTerminalContact)
            )}
          </p>
        </div>
        <div className="mb-5">
          <h3 className="text-base font-semibold">
            Destination Service Center
          </h3>
          <p className="text-base">
            {loading ? (
              <Skeleton count={3} />
            ) : (
              getTerminalContactText(destinationTerminalContact)
            )}
          </p>
        </div>
      </div>
      <p className="mt-14">
        Please note that transit times are estimates and can vary due to factors
        like the actual starting location, destination, and the specific day of
        the week. Service days do not include day of pickup, weekends, or
        holidays
      </p>
    </>
  )
}

export const TransitTimeCalculatorResult: React.FunctionComponent<
  TransitTimeCalculatorResultProps
> = ({ loading, result }) => {
  const { error } = result
  const showError = !!error && !loading

  return (
    <div className="px-6 py-16 sm:px-10">
      <ResultHeaderCard
        loading={loading}
        result={result}
        showError={showError}
      />
      <div className="sm:px-6">
        {showError ? (
          <ErrorResultBody />
        ) : (
          <SuccessResultBody loading={loading} result={result} />
        )}
      </div>
    </div>
  )
}
